import React, { useEffect, useState } from 'react'
import DateRange, { DatePick } from '../components/DateRange.jsx'
import { portal } from '../portalApi.js'
import { currentCompany } from '../company.js'
import { fmt } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'

// 클로브 자금현황처럼 기간을 단추로 먼저 고른다. 날짜를 직접 집는 일보다 «이번 달»
// 처럼 통째로 보는 일이 훨씬 잦다.
const PERIODS = [['day', '일'], ['week', '주'], ['month', '월'],
                 ['quarter', '분기'], ['year', '연']]

// 보던 날이 든 기간을 잡는다. 오늘이 아니라 «보던 날» 기준이어야, 지난 달을 보다가
// «월» 을 눌렀을 때 이번 달로 튀지 않는다.
function 기간잡기(단위, 기준일) {
  const d = new Date(기준일)
  const p = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
  if (단위 === 'week') {
    const 월요일 = new Date(d); 월요일.setDate(d.getDate() - ((d.getDay() + 6) % 7))
    const 일요일 = new Date(월요일); 일요일.setDate(월요일.getDate() + 6)
    return [p(월요일), p(일요일)]
  }
  if (단위 === 'month') {
    return [p(new Date(d.getFullYear(), d.getMonth(), 1)),
            p(new Date(d.getFullYear(), d.getMonth() + 1, 0))]
  }
  if (단위 === 'quarter') {
    const q = Math.floor(d.getMonth() / 3)
    return [p(new Date(d.getFullYear(), q * 3, 1)),
            p(new Date(d.getFullYear(), q * 3 + 3, 0))]
  }
  return [p(new Date(d.getFullYear(), 0, 1)), p(new Date(d.getFullYear(), 11, 31))]
}

// 자금일보 기준일은 전일이다. 오늘 것은 은행이 아직 다 넘겨주지 않아 하루 내내 반쪽으로
// 보이고, 보고도 어제 자금으로 한다. 사람이 날짜를 고르면 그때는 그 날을 따른다.
// 주소로 날짜를 받는다 — #/treasury/daily?date=2026-10-08
//                      #/treasury/daily?from=2026-10-09&to=2026-10-11
//
// 보고용 화면을 그대로 찍어 노션에 올리는데(fa/screenshot.py), 찍는 쪽이 날짜를
// 고를 길이 없으면 늘 어제치만 나온다. 월요일에 금~일을 묶어 올릴 수가 없다.
// 사람에게도 쓸모가 있다 — 특정 날짜 화면을 그대로 주소로 건넬 수 있다.
const 날꼴 = /^\d{4}-\d{2}-\d{2}$/
function 주소조건() {
  const h = window.location.hash || ''
  if (!h.includes('?')) return null
  const q = new URLSearchParams(h.slice(h.indexOf('?') + 1))
  const [d, f, t] = [q.get('date'), q.get('from'), q.get('to')]
  if (날꼴.test(f || '') && 날꼴.test(t || '')) return { range: [f, t] }
  if (날꼴.test(d || '')) return { date: d }
  return null
}

const yesterday = () => {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// 계좌유형 > 사업부 > 계좌 그룹핑 (포털 자금일보와 동일 구조)
// 기초잔액도 입출금도 잔액도 모두 0인 계좌. 그날 아무 일도 없었고 남은 돈도 없다.
// 보고서에서는 자리만 차지하므로 접을 수 있게 한다.
function AcctTxModal({ acct, date, onClose }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])
  const rows = acct['거래'] || []
  const 입금 = rows.filter((t) => t['금액'] > 0)
  const 출금 = rows.filter((t) => t['금액'] < 0)
  const 합 = (list) => list.reduce((s, t) => s + Math.abs(t['금액']), 0)
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel acct-tx" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{acct['계좌']}
            <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>
              {' '}· {date} · {acct['계좌유형']}/{acct['사업부']}
            </span>
          </h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="acct-tx-sum">
          <span>전일잔액 <b>{fmt(acct['전일잔액'])}</b></span>
          <span>입금 {입금.length}건 <b className="pos">{fmt(합(입금))}</b></span>
          <span>출금 {출금.length}건 <b className="neg">{fmt(합(출금))}</b></span>
          <span>금일잔액 <b>{fmt(acct['금일잔액'])}</b></span>
        </div>
        <div className="modal-table-wrap">
          <table className="modal-table">
            <thead>
              <tr>
                <th style={{ width: 56 }}>시각</th>
                <th style={{ width: 82 }}>구분</th>
                <th className="l">상대</th>
                <th style={{ width: 120, textAlign: 'right' }}>금액</th>
                <th style={{ width: 130, textAlign: 'right' }}>거래후잔액</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="c muted" style={{ padding: 20 }}>그날 거래가 없습니다</td></tr>
              )}
              {rows.map((t, i) => (
                <tr key={i}>
                  <td className="muted">{t['시각']}</td>
                  <td className="muted">{t['구분']}</td>
                  <td className="l">{t['상대'] || <span className="muted">(상대 없음)</span>}</td>
                  <td style={{ textAlign: 'right' }} className={t['금액'] < 0 ? 'neg' : 'pos'}>{fmt(t['금액'])}</td>
                  <td style={{ textAlign: 'right' }} className="muted">{t['잔액'] == null ? '' : fmt(t['잔액'])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <span className="muted">거래 {rows.length}건</span>
          <span style={{ marginLeft: 'auto' }} />
          <button type="button" className="btn secondary" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  )
}

const 원화인가 = (a) => (a['통화'] || 'KRW').toUpperCase() === 'KRW'

function AccountSettingsModal({ onClose, onSaved }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [dirty, setDirty] = useState({})

  const load = () => {
    portal.get('/api/accounts/settings').then(setData).catch((e) => setErr(e.message))
  }
  useEffect(() => { load() }, [])

  const edit = (id, patch) => setDirty((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }))
  const valueOf = (a, key) => (dirty[a.assetId]?.[key] ?? a[key] ?? '')

  // 여러 계좌를 손보고 한 번에 저장한다 — 줄마다 저장을 누르게 하면 고치다 말고 놓친다
  const changedCount = Object.keys(dirty).length

  const saveAll = async () => {
    if (!changedCount) return
    setBusy(true); setErr('')
    try {
      for (const a of data.accounts) {
        const d = dirty[a.assetId]
        if (!d) continue
        if ('excluded' in d) {
          await portal.post('/api/accounts/exclude', {
            assetId: a.assetId, bankName: a.bankName, accountNo: a.accountNo,
            nickname: a.nickname, excluded: d.excluded,
          })
        }
        if ('alias' in d || 'category' in d || 'businessUnit' in d) {
          await portal.post('/api/accounts/settings', {
            assetId: a.assetId, bankName: a.bankName, accountNo: a.accountNo,
            nickname: a.nickname,
            alias: valueOf(a, 'alias'), category: valueOf(a, 'category'),
            businessUnit: valueOf(a, 'businessUnit'),
            vendorCode: a.vendorCode, vendorName: a.vendorName,
          })
        }
      }
      setDirty({})
      load(); onSaved()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  // 고쳐 놓고 그냥 닫으면 조용히 사라진다
  const tryClose = () => {
    if (changedCount && !window.confirm(`저장하지 않은 변경 ${changedCount}건이 있습니다. 닫을까요?`)) return
    onClose()
  }

  return (
    <div className="modal-backdrop" onMouseDown={tryClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 900 }}>
        <div className="modal-header">
          <h3>계좌 설정</h3>
          <button type="button" className="modal-close" onClick={tryClose}>×</button>
        </div>
        <div style={{ padding: 18 }}>
          <div className="hint" style={{ marginBottom: 10 }}>
            계좌유형이 자금일보의 묶음과 소계를 정합니다. 사업부는 그 안에서 다시 나눕니다.
            우리 자산이 아닌 계좌(퇴직연금신탁 등)나 더 안 쓰는 계좌는 <b>제외</b>로 두면
            자금일보에서 아예 빠집니다.
          </div>
          {err && <div className="error-box" style={{ marginBottom: 10 }}>{err}</div>}
          {!data && !err && <div className="muted">불러오는 중…</div>}
          {data && (
            <div style={{ maxHeight: '58vh', overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th className="l">계좌</th><th className="l">별칭</th>
                    <th>계좌유형</th><th>사업부</th><th>제외</th>
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.map((a) => (
                      <tr key={a.assetId}
                        className={dirty[a.assetId] ? 'acct-dirty' : (a.excluded ? 'muted' : undefined)}>
                        <td className="l">
                          {a.shortLabel}
                          <span className="muted" style={{ fontSize: 11 }}>
                            {' '}· {a.bankName} {a.accountNo}{a.nickname ? ` · ${a.nickname}` : ''}
                          </span>
                        </td>
                        <td className="l">
                          <input value={valueOf(a, 'alias')} style={{ width: 130 }}
                            placeholder={a.shortLabel}
                            onChange={(e) => edit(a.assetId, { alias: e.target.value })} />
                        </td>
                        <td className="c">
                          <select value={valueOf(a, 'category')}
                            onChange={(e) => edit(a.assetId, { category: e.target.value })}>
                            <option value="">— 미지정 —</option>
                            {data.categories.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                        </td>
                        <td className="c">
                          <select value={valueOf(a, 'businessUnit')}
                            onChange={(e) => edit(a.assetId, { businessUnit: e.target.value })}>
                            <option value="">— 미지정 —</option>
                            {data.businessUnits.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                        </td>
                        <td className="c">
                          <input type="checkbox"
                            checked={dirty[a.assetId]?.excluded ?? a.excluded}
                            onChange={(e) => edit(a.assetId, { excluded: e.target.checked })} />
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
            <span className="muted" style={{ fontSize: 12 }}>
              {changedCount ? `${changedCount}개 계좌를 고쳤습니다 — 저장해야 반영됩니다` : ''}
            </span>
            <span>
              <button type="button" className="btn secondary" onClick={tryClose}>닫기</button>{' '}
              <button type="button" className="btn" disabled={busy || !changedCount}
                onClick={saveAll}>{busy ? '저장 중…' : '저장'}</button>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// 자금일보 표 한 벌. 사업자별로 따로 보려면 회사마다 이걸 한 번씩 세운다 -
// 한 표에 회사를 쌓으면 소계 줄이 이어져 어디까지가 그 회사인지 눈으로 못 가른다.
// ───────────────────────────────────────────────────────────────────────────
// 경영진 보고용 구성 — 원화 / 외화 / 총 보유자금 / 차입금 / 계좌 상세
//
// 섞지 않는 것이 이 화면의 전부다.
//
//   원화와 외화를 한 칸에 더하지 않는다. 외화는 통화 단위가 뜻이고 원화 환산액은
//   환율이 움직이면 같이 움직인다. 더해도 되는 자리는 「총 보유자금」 한 줄뿐이다.
//
//   현금과 내부대체를 섞지 않는다. 10/06 에 「당일 입금 3.5억」으로 읽히던 것 중
//   1.21억은 우리 계좌끼리 옮긴 돈이었다. 요약은 현금만 세고 대체는 상세에 둔다.
//   다만 사업부 사이에서는 대체가 상계되지 않으므로(에르모어 → 링크맘 1.04억)
//   사업부 표에는 그 열이 있어야 기초 + 입금 − 출금 + 대체 = 기말이 맞는다.
//
//   회사를 섞지 않는다. 전사 총합은 만들지 않는다 - 법인이 다르면 남의 돈이다.
// ───────────────────────────────────────────────────────────────────────────

// 법인 꼬리말을 떼어 탭에 쓴다. 「주식회사 에르모어」는 탭 한 칸을 다 먹는다.
const 짧은회사 = (n) => (n || '').replace(/^(주식회사|\(주\))\s*/, '').replace(/\s*(주식회사|\(주\))$/, '').trim()

// 사업부 표시 이름. DB 값(에르모어·링크맘·기타)은 그대로 두고 화면에서만 바꾼다 -
// 값을 고치면 계좌 설정·엑셀·다른 화면이 함께 바뀐다.
const 사업부이름 = (u) => ({ 에르모어: '에르모어사업부', 링크맘: '링크맘사업부' }[u]
  || (u && u !== '기타' ? u : '관계회사'))

// 사업부와 은행 차례는 사람이 정한다. 금액 순으로 세우면 같은 통장이 날마다 자리를
// 옮겨서, 늘 보던 자리에서 못 찾는다. 보고서는 같은 자리에 같은 줄이 있어야 읽힌다.
const 사업부차례 = ['에르모어', '링크맘']
const 사업부순 = (u) => {
  const i = 사업부차례.indexOf(u)
  return i < 0 ? 사업부차례.length : i
}
const 은행차례 = ['국민', '기업', '신한', '농협', '우리', '한투']
const 은행순 = (계좌) => {
  const i = 은행차례.indexOf(String(계좌 || '').split(' / ')[0].trim())
  return i < 0 ? 은행차례.length : i
}
const 계좌순 = (a, b) => 은행순(a['계좌']) - 은행순(b['계좌'])
  || String(a['계좌'] || '').localeCompare(String(b['계좌'] || ''), 'ko')

const 대출인가 = (a) => (a['계좌유형'] || '') === '대출'
const 자금계좌 = (list) => (list || []).filter((a) => !대출인가(a))

// 요약이 세는 것과 표가 세는 것이 한 함수에서 나와야 둘이 어긋나지 않는다.
function 집계(rows) {
  const s = { 기초: 0, 입금: 0, 출금: 0, 대체: 0, 환율변동: 0, 기말: 0 }
  for (const a of rows || []) {
    s.기초 += a['전일잔액'] || 0
    s.입금 += a['금일입금현금'] || 0
    s.출금 += a['금일출금현금'] || 0
    s.대체 += (a['금일입금대체'] || 0) - (a['금일출금대체'] || 0)
    s.환율변동 += a['환율변동'] || 0
    s.기말 += a['금일잔액'] || 0
  }
  return s
}
const 움직였나 = (s) => ['기초', '입금', '출금', '대체', '기말'].some((k) => Math.abs(s[k]) >= 0.5)

// 외화 계좌의 통화 단위 금액. 화면에 선 원화를 그 원화를 만든 환율로 되나눈다 -
// 다른 환율로 나누면 「금액 × 환율 ≠ 원화」가 되어 어느 쪽을 믿을지 모르게 된다.
// 기초만 전일 환율을 쓴다(그날 환산한 값이라 그래야 통화 단위로 식이 맞는다).
function 외화집계(rows) {
  const s = { 기초: 0, 입금: 0, 출금: 0, 대체: 0, 기말: 0 }
  for (const a of rows || []) {
    const r = a['환율'] || 0
    const r0 = a['전일환율'] || r
    if (r0) s.기초 += (a['전일잔액'] || 0) / r0
    if (r) {
      s.입금 += (a['금일입금현금'] || 0) / r
      s.출금 += (a['금일출금현금'] || 0) / r
      s.대체 += ((a['금일입금대체'] || 0) - (a['금일출금대체'] || 0)) / r
      s.기말 += (a['금일잔액'] || 0) / r
    }
  }
  return s
}

// 한 계좌의 통화 단위 검산. 기초 + 흐름 = 기말이어야 한다 - 원화로 보면 환율이
// 움직인 날마다 어긋나므로, 빠진 거래를 가리려면 통화 단위로 봐야 한다.
function 통화검산(a) {
  const r = a['환율'] || 0
  const r0 = a['전일환율'] || r
  if (!r || !r0) return 0
  const 흐름 = ((a['금일입금현금'] || 0) + (a['금일입금대체'] || 0)
    - (a['금일출금현금'] || 0) - (a['금일출금대체'] || 0)) / r
  return (a['금일잔액'] || 0) / r - (a['전일잔액'] || 0) / r0 - 흐름
}

// 원화 환산액이 기초에서 기말로 가며 벌어지는 몫을 둘로 가른다.
//
//   환율변동   기초 외화 × (기말 환율 − 기초 환율). 달러는 그대로인데 재는 자가 바뀐 것.
//   자료 차이  통화 단위로도 안 맞는 몫. 거래가 빠진 것이지 환율 탓이 아니다.
//
// 한 칸으로 두면 환율변동이 빠진 자료까지 메우는 칸이 되어 식은 늘 맞아떨어진다.
// 한투 USD 94.14(12.6만 원)가 그렇게 숨어 있었다.
function 환산갈래(rows) {
  let 환율변동 = 0
  let 자료차이 = 0
  for (const a of rows || []) {
    const r = a['환율'] || 0
    const r0 = a['전일환율'] || r
    if (!r || !r0) continue
    환율변동 += ((a['전일잔액'] || 0) / r0) * (r - r0)
    자료차이 += 통화검산(a) * r
  }
  return { 환율변동, 자료차이 }
}

const 묶기 = (rows, key) => {
  const m = new Map()
  for (const a of rows) {
    const k = a[key] || '기타'
    if (!m.has(k)) m.set(k, [])
    m.get(k).push(a)
  }
  return m
}

// 접었다 펴는 자리. details 를 쓰지 않는 이유는 인쇄다 - 접힌 채 PDF 를 뽑으면
// 계좌 상세가 통째로 빠진다. 화면에서만 접고, 인쇄에는 늘 펼쳐 내보낸다.
function 접는칸({ 제목, 꼬리, 기본열림 = false, children }) {
  const [열림, set열림] = useState(기본열림)
  return (
    <div className={'td-fold' + (열림 ? ' on' : '')}>
      <button type="button" className="td-fold-head no-print" onClick={() => set열림(!열림)}>
        <span className="td-fold-arrow">{열림 ? '▾' : '▸'}</span>
        <span className="td-fold-title">{제목}</span>
        {꼬리 && <span className="muted td-fold-tail">{꼬리}</span>}
      </button>
      <div className="td-fold-body">{children}</div>
    </div>
  )
}

function 섹션({ 제목, 단위, 꼬리, children }) {
  return (
    <section className="td-sec">
      <div className="td-sec-top">
        <h3 className="td-sec-head">{제목}</h3>
        {꼬리 && <span className="muted td-sec-note">{꼬리}</span>}
        {단위 && <span className="muted td-sec-unit">단위: {단위}</span>}
      </div>
      {children}
    </section>
  )
}

// 요약 카드 넷 — 기초 → 입금 → 출금 → 기말. 원화와 외화가 같은 모양을 쓴다.
// 꼬리(델타·환율변동)는 기말 칸 아래 한 줄로만 둔다. 칸을 하나 더 세우면 두 섹션의
// 모양이 갈리고, 갈리면 "이 둘은 다른 것"으로 읽힌다.
function 요약카드({ s, 기간, 꼬리 }) {
  const 칸 = [
    ['기초잔액', s.기초],
    [기간 ? '총 입금' : '당일 입금', s.입금],
    [기간 ? '총 출금' : '당일 출금', s.출금],
  ]
  return (
    <div className="td-kpi">
      {칸.map(([이름, v]) => (
        <div className="td-kpi-cell" key={이름}>
          <div className="td-kpi-cap">{이름}</div>
          <div className="td-kpi-val">{fmt(v)}</div>
        </div>
      ))}
      <div className="td-kpi-cell lead">
        <div className="td-kpi-cap">기말잔액</div>
        <div className="td-kpi-val">{fmt(s.기말)}</div>
        {꼬리 && <div className="td-kpi-delta">{꼬리}</div>}
      </div>
    </div>
  )
}

// 묶음 줄을 누르면 그 아래 계좌가 펼쳐진다. 계좌 상세를 맨 아래 따로 두면 숫자를
// 보다가 찾으러 내려가야 하는데, 그 사이에 무엇을 보려 했는지 잊는다.
function 펼침표시({ 열림 }) {
  return <span className="td-ch" aria-hidden="true">{열림 ? '▾' : '▸'}</span>
}

const 계좌정렬 = (list) => list.filter((a) => 움직였나(집계([a]))).sort(계좌순)

// 계좌 줄에 붙는 경고. 소계만 보고는 어느 통장이 어긋났는지 알 수 없다.
function 계좌경고({ a, 외화 }) {
  const 검 = 외화 ? 통화검산(a) : 0
  return (
    <>
      {a['전일잔액불명'] && <span className="warn" title="전일잔액을 찾지 못했습니다"> ⚠</span>}
      {외화 && Math.abs(검) >= 0.01 && (
        <span className="warn" title="기초 + 입금 − 출금 = 기말이 이만큼 어긋납니다. 환율 탓이 아니라 거래가 빠진 것입니다.">
          {' '}⚠ {fmt(검, 2)}
        </span>
      )}
      {!외화 && !!a['잔액오차'] && (
        <span className="warn" title="기초 + 입금 − 출금이 기말과 어긋납니다 — 거래가 빠졌거나 방향이 틀렸습니다">
          {' '}⚠ {fmt(a['잔액오차'])}
        </span>
      )}
    </>
  )
}

// ① 원화 자금 현황
function 원화영역({ rows, 기간, 회사, 펼침, 토글, onOpenAcct }) {
  const 합 = 집계(rows)
  const 묶음 = [...묶기(rows, '사업부').entries()]
    .map(([u, list]) => ({ 키: 'w:' + u, 원래: u, 이름: 사업부이름(u),
                           s: 집계(list), 계좌: 계좌정렬(list) }))
    .filter((x) => 움직였나(x.s))
    .sort((a, b) => 사업부순(a.원래) - 사업부순(b.원래) || b.s.기말 - a.s.기말)
  const 대체있나 = rows.some((a) => Math.abs((a['금일입금대체'] || 0) - (a['금일출금대체'] || 0)) >= 0.5)
  const 증감 = 합.기말 - 합.기초
  const 대체칸 = (v) => (
    <td>{Math.abs(v) < 0.5 ? '–' : `${v > 0 ? '+' : '−'}${fmt(Math.abs(v))}`}</td>
  )
  return (
    <섹션 제목="원화 자금 현황" 단위="원">
      <요약카드 s={합} 기간={기간}
        꼬리={Math.abs(증감) < 0.5 ? '변동 없음'
          : `${증감 > 0 ? '▲' : '▼'} ${fmt(Math.abs(증감))}`} />
      <table className="td-rep">
        <thead>
          <tr>
            <th className="l">사업부</th>
            <th>기초잔액</th>
            <th>{기간 ? '총 입금' : '당일 입금'}</th>
            <th>{기간 ? '총 출금' : '당일 출금'}</th>
            {대체있나 && <th>사업부 간 대체</th>}
            <th>기말잔액</th>
          </tr>
        </thead>
        <tbody>
          {묶음.map((x) => {
            const 열림 = 펼침.has(x.키)
            return (
              <React.Fragment key={x.키}>
                <tr className="grp" onClick={() => 토글(x.키)}>
                  <td className="l"><펼침표시 열림={열림} />{x.이름}
                    <span className="sub">계좌 {x.계좌.length}</span>
                  </td>
                  <td>{fmt(x.s.기초)}</td>
                  <td>{fmt(x.s.입금)}</td>
                  <td>{fmt(x.s.출금)}</td>
                  {대체있나 && 대체칸(x.s.대체)}
                  <td>{fmt(x.s.기말)}</td>
                </tr>
                {x.계좌.map((a) => {
                  const s = 집계([a])
                  return (
                    <tr key={a['계좌']}
                      className={'acct' + (열림 ? '' : ' folded')
                        + (a['거래']?.length ? ' acct-open' : '')}
                      onClick={(e) => { e.stopPropagation(); a['거래']?.length && onOpenAcct(a) }}>
                      <td className="l">{a['계좌']}
                        {a['별칭'] && <span className="sub">{a['별칭']}</span>}
                        <계좌경고 a={a} />
                      </td>
                      <td>{fmt(s.기초)}</td>
                      <td>{s.입금 ? fmt(s.입금) : '–'}</td>
                      <td>{s.출금 ? fmt(s.출금) : '–'}</td>
                      {대체있나 && 대체칸(s.대체)}
                      <td>{fmt(s.기말)}</td>
                    </tr>
                  )
                })}
              </React.Fragment>
            )
          })}
          <tr className="sum">
            <td className="l">{짧은회사(회사)} 원화 합계</td>
            <td>{fmt(합.기초)}</td>
            <td>{fmt(합.입금)}</td>
            <td>{fmt(합.출금)}</td>
            {대체있나 && 대체칸(합.대체)}
            <td>{fmt(합.기말)}</td>
          </tr>
        </tbody>
      </table>
      {대체있나 && (
        <div className="muted td-note">
          입금·출금은 현금만 셉니다. 사업부끼리 옮긴 돈은 「사업부 간 대체」에 따로 섰고,
          회사 전체로는 서로 상계되어 합계가 0 입니다.
        </div>
      )}
    </섹션>
  )
}

// 통화 줄에 «우리가 얼마에 사 왔나» 를 같이 세운다. 매매기준율만 보여 주면 그날
// 시장이 얼마였나만 알 뿐이다 - 외화를 들고 있는 쪽이 궁금한 것은 취득원가다.
//
// 「취득」은 외화예금 원장의 총평균단가 = 지금 들고 있는 그 외화의 원가다. 원장이
// 미확정이면 대신 「환전 평균」을 보여 준다 - 그쪽은 이미 써 버린 돈까지 들어 있어
// 보유분 원가와 다르다(USD 1,456 대 1,363). 다르다는 것을 이름으로 밝혀 둔다.
function 취득환율({ m, 고시 }) {
  if (!m) return null
  const 장부 = m.장부단가 && !m.장부미확정 ? m.장부단가 : null
  const 값 = 장부 || m.환율
  if (!값) return null
  const 차이 = 고시 ? (고시 - 값) / 값 * 100 : null
  const 설명 = (장부
    ? [`외화예금 원장의 총평균단가 — 지금 들고 있는 ${fmt(m.장부잔액, 2)} 의 원가입니다.`,
       '들어올 때마다 그때 잔액에 섞이므로 최근 환전이 더 크게 반영됩니다.',
       `참고: 환전만 모은 평균은 ${fmt(m.환율, 2)} 입니다 (${m.건수}건 ${m.첫날}~${m.끝날},`
       + ' 이미 써 버린 돈까지 들어 있습니다).']
    : [`환전 ${m.건수}건 가중평균 · ${m.첫날} ~ ${m.끝날}`,
       `원화 ${fmt(m.원화)} ÷ 외화 ${fmt(m.외화, 2)}`,
       '원장 단가가 아직 안 섭니다 — 취득환율 미지정 건이 있습니다(자금 › 외화예금).']
  ).concat(['한국투자증권 체결환율에서 옵니다.']).join('\n')
  return (
    <span className="sub td-fxbuy" title={설명}>
      {장부 ? '취득' : '환전평균'} {fmt(값, 2)}
      {차이 != null && Math.abs(차이) >= 0.05 && (
        <b className={차이 > 0 ? 'ok' : 'warn'}>
          {' '}{차이 > 0 ? '▲' : '▼'}{fmt(Math.abs(차이), 1, 1)}%
        </b>
      )}
    </span>
  )
}

// ② 외화 자금 현황 — 카드는 원화 환산, 표는 통화 단위
function 외화영역({ rows, 기간, 환전평균, 펼침, 토글, onOpenAcct }) {
  if (!rows.length) return null
  const 원화합 = 집계(rows)
  const 갈래 = 환산갈래(rows)
  const 기준일 = rows.map((a) => a['환율기준일']).find(Boolean)
  const 통화들 = [...묶기(rows, '통화').entries()]
    .map(([cur, list]) => ({
      키: 'f:' + cur, 통화: cur, 계좌: 계좌정렬(list),
      f: 외화집계(list), w: 집계(list),
      환율: list.map((a) => a['환율']).find(Boolean) || null,
    }))
    .filter((x) => 움직였나(x.w))
    .sort((a, b) => b.w.기말 - a.w.기말)
  if (!통화들.length) return null
  const 대체있나 = rows.some((a) => Math.abs((a['금일입금대체'] || 0) - (a['금일출금대체'] || 0)) >= 0.5)
  const 원 = (v) => (Math.abs(v) < 0.005 ? '–' : fmt(v, 2))
  // 취득단가는 통화 하나에 하나다(같은 USD 면 어느 통장에 있든 원가가 같다 - 외화예금
  // 원장이 그렇게 센다). 그래서 계좌 줄에도 같은 단가를 곱한다.
  const 단가 = (통화) => {
    const m = (환전평균 || {})[통화]
    if (!m) return null
    return m.장부단가 && !m.장부미확정 ? m.장부단가 : null
  }
  const 취득원화 = (통화, 외화기말) => {
    const d = 단가(통화)
    return d ? Math.round(외화기말 * d) : null
  }
  const 비교칸 = (통화, 외화기말, 환산원화) => {
    const 취득 = 취득원화(통화, 외화기말)
    const 차 = 취득 == null ? null : 환산원화 - 취득
    return (
      <>
        <td>{취득 == null ? '–' : fmt(취득)}</td>
        <td className={차 == null ? undefined : (차 >= 0 ? 'td-fxup' : 'td-fxdown')}>
          {차 == null ? '–'
            : (Math.abs(차) < 1 ? '–' : `${차 > 0 ? '+' : '−'}${fmt(Math.abs(차))}`)}
        </td>
      </>
    )
  }
  const 외화칸 = (s) => (
    <>
      <td>{fmt(s.기초, 2)}</td>
      <td>{원(s.입금)}</td>
      <td>{원(s.출금)}</td>
      {대체있나 && (
        <td>{Math.abs(s.대체) < 0.005 ? '–'
          : `${s.대체 > 0 ? '+' : '−'}${fmt(Math.abs(s.대체), 2)}`}</td>
      )}
      <td>{fmt(s.기말, 2)}</td>
    </>
  )
  // 회사 전체 평가차 - 단가가 없는 통화는 빼고 더한다. 빠진 통화는 아래 설명에 적는다.
  const 평가 = 통화들.reduce((a, x) => {
    const 취득 = 취득원화(x.통화, x.f.기말)
    return 취득 == null ? a : { 취득: a.취득 + 취득, 환산: a.환산 + x.w.기말 }
  }, { 취득: 0, 환산: 0 })
  const 단가없는통화 = 통화들.filter((x) => 단가(x.통화) == null).map((x) => x.통화)

  // 외화가 한 푼도 안 움직여도 환율이 움직이면 원화 환산액은 달라진다. 그 몫을
  // 기말 칸 아래 적지 않으면 「네 칸이 안 맞는다」로 읽힌다.
  const 꼬리 = [
    Math.abs(갈래.환율변동) >= 1
      ? `환율변동 ${갈래.환율변동 > 0 ? '+' : '−'}${fmt(Math.abs(갈래.환율변동))}` : '',
    Math.abs(갈래.자료차이) >= 1
      ? `자료 차이 ${갈래.자료차이 > 0 ? '+' : '−'}${fmt(Math.abs(갈래.자료차이))}` : '',
  ].filter(Boolean).join(' · ')
  return (
    <섹션 제목="외화 자금 현황"
      꼬리={기준일 ? `환율 기준일 ${기준일} · 서울외국환중개 매매기준율` : '환율 자료 없음'}
      단위="카드 원화 · 표 통화">
      <요약카드 s={원화합} 기간={기간} 꼬리={꼬리 || null} />
      <table className="td-rep td-fx">
        <thead>
          <tr>
            <th className="l">통화</th>
            <th>기초</th>
            <th>{기간 ? '총 입금' : '당일 입금'}</th>
            <th>{기간 ? '총 출금' : '당일 출금'}</th>
            {대체있나 && <th>대체</th>}
            <th>기말</th>
            <th>원화 환산액</th>
            <th>취득 환산</th>
            <th>차이</th>
          </tr>
        </thead>
        <tbody>
          {통화들.map((x) => {
            const 열림 = 펼침.has(x.키)
            return (
              <React.Fragment key={x.키}>
                <tr className="grp" onClick={() => 토글(x.키)}>
                  <td className="l"><펼침표시 열림={열림} /><b>{x.통화}</b>
                    <span className="sub">
                      {x.환율 ? `× ${fmt(x.환율, 2)} · ` : ''}계좌 {x.계좌.length}
                    </span>
                    <취득환율 m={(환전평균 || {})[x.통화]} 고시={x.환율} />
                  </td>
                  {외화칸(x.f)}
                  <td>{fmt(x.w.기말)}</td>
                  {비교칸(x.통화, x.f.기말, x.w.기말)}
                </tr>
                {x.계좌.map((a) => (
                  <tr key={a['계좌']}
                    className={'acct' + (열림 ? '' : ' folded')
                      + (a['거래']?.length ? ' acct-open' : '')}
                    onClick={(e) => { e.stopPropagation(); a['거래']?.length && onOpenAcct(a) }}>
                    <td className="l">{a['계좌'].replace(/ · [A-Z]{3}$/, '')}
                      {a['별칭'] && <span className="sub">{a['별칭']}</span>}
                      {a['마지막거래일'] && <span className="sub">{a['마지막거래일'].slice(5)}</span>}
                      <계좌경고 a={a} 외화 />
                    </td>
                    {외화칸(외화집계([a]))}
                    <td>{fmt(a['금일잔액'])}</td>
                    {비교칸(a['통화'], 외화집계([a]).기말, a['금일잔액'] || 0)}
                  </tr>
                ))}
              </React.Fragment>
            )
          })}
          <tr className="sum">
            <td className="l">외화 환산 합계</td>
            <td colSpan={대체있나 ? 5 : 4} />
            <td>{fmt(원화합.기말)}</td>
            <td>{평가.취득 ? fmt(평가.취득) : '–'}</td>
            <td className={!평가.취득 ? undefined
              : (평가.환산 - 평가.취득 >= 0 ? 'td-fxup' : 'td-fxdown')}>
              {평가.취득
                ? `${평가.환산 - 평가.취득 > 0 ? '+' : '−'}${fmt(Math.abs(평가.환산 - 평가.취득))}`
                : '–'}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="muted td-note">
        표의 금액은 통화 단위, 「원화 환산액」과 위 카드만 원입니다. 계좌 옆 날짜는 마지막
        거래일입니다 — 오래된 통화는 자료가 밀린 것입니다(한국투자증권은 수기 엑셀 업로드).
        <br />
        <b>취득 환산</b>은 기말 외화를 취득단가로 곱한 원화 — 우리가 들여올 때 들어간 원화이고,
        <b>원화 환산액</b>은 오늘 매매기준율로 다시 잰 값입니다. <b>차이</b>가 아직 실현되지
        않은 환차손익입니다(쓰거나 팔 때 실현됩니다).
        {단가없는통화.length > 0 && ` 단가가 아직 안 서는 ${단가없는통화.join('·')} 는 비교에서 빠집니다.`}
        <br />
        <b>취득</b>은 지금 들고 있는 그 외화를 얼마에 샀는지(외화예금 원장의 총평균단가),
        <b>×</b> 는 그날 매매기준율입니다. 옆의 ▲▼ 는 둘의 차이로, 지금 환율이 사 올 때보다
        오르면 ▲ 입니다. 원장 단가가 아직 안 서는 통화는 <b>환전평균</b>(환전한 것 전부의
        가중평균)을 대신 보여 줍니다 — 이미 쓴 돈까지 들어 있어 보유분 원가보다 높게
        나옵니다. 어느 쪽이든 한국투자증권 체결환율에서 옵니다.
      </div>
    </섹션>
  )
}

// ③ 둘을 더해도 되는 유일한 자리. 맨 위에 둔다 - 아래에 두면 원화 기말잔액이 제일 큰
// 글씨라 그게 회사 돈 전부로 읽힌다(실제로 그렇게 읽혔다).
function 총보유({ 회사, 원화, 외화, 대출, 날짜 }) {
  const w = 집계(원화).기말
  const f = 집계(외화).기말
  return (
    <div className="td-total">
      <div className="td-total-cap">{짧은회사(회사)} 총 보유자금
        <span className="muted"> · {f ? '외화 원화환산 포함 · ' : ''}{날짜}</span>
      </div>
      <div className="td-total-val">{fmt(w + f)} <span className="td-kpi-unit">원</span></div>
      <div className="td-total-parts">
        <div><span className="muted">원화</span><b>{fmt(w)}</b></div>
        {!!f && <div><span className="muted">외화 환산</span><b>{fmt(f)}</b></div>}
        {!!대출 && <div><span className="muted">차입금</span><b className="muted">{fmt(대출)}</b></div>}
      </div>
    </div>
  )
}

// ④ 차입금 — 보유자금과 떨어진 자리에 둔다. 선택한 회사 것만.
function 차입금영역({ loanTotals, accounts, onOpenAcct }) {
  const 대출 = (accounts || []).filter(대출인가)
    .filter((a) => (a['금일잔액'] || 0) || (a['전일잔액'] || 0))
    .sort(계좌순)
  const 잔액 = loanTotals?.['금일잔액'] || 0
  if (!잔액 && !대출.length) return null
  const 증감 = 잔액 - (loanTotals?.['전일잔액'] || 0)
  return (
    <섹션 제목="차입금 현황" 단위="원" 꼬리="갚아야 할 돈이라 보유자금에 넣지 않습니다">
      <div className="td-loan">
        <div className="td-loan-val">{fmt(잔액)} <span className="td-kpi-unit">원</span></div>
        <div className="muted">{Math.abs(증감) < 0.5 ? '변동 없음'
          : `${증감 > 0 ? '+' : '−'}${fmt(Math.abs(증감))}원`}</div>
      </div>
      {!!대출.length && (
        <접는칸 제목={`차입 계좌 ${대출.length}건`}>
          <table className="td-rep">
            <thead>
              <tr><th className="l">계좌</th><th>기초잔액</th><th>증감</th><th>기말잔액</th></tr>
            </thead>
            <tbody>
              {대출.map((a) => (
                <tr key={a['계좌']} className={a['거래']?.length ? 'acct-open' : undefined}
                  onClick={() => a['거래']?.length && onOpenAcct(a)}>
                  <td className="l">{a['계좌']}
                    {a['별칭'] && <span className="muted"> · {a['별칭']}</span>}
                  </td>
                  <td>{fmt(a['전일잔액'])}</td>
                  <td>{fmt((a['금일잔액'] || 0) - (a['전일잔액'] || 0))}</td>
                  <td className="lead">{fmt(a['금일잔액'])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </접는칸>
      )}
    </섹션>
  )
}

// 한 회사 보고서 한 벌. 화면에서도 인쇄에서도 이 순서다 - 요약이 앞, 계좌 상세가 뒤.
// 한 회사 보고서 한 벌. 화면에서도 인쇄에서도 이 순서다 - 총액이 맨 앞, 대체 내역이 뒤.
//
// 「계좌 상세」 섹션은 없앴다. 계좌는 사업부·통화 줄을 눌러 그 자리에서 편다 -
// 숫자를 보다가 상세를 찾아 맨 아래로 내려가면, 그 사이에 무엇을 보려 했는지 잊는다.
function 회사보고({ co, 기간, onOpenAcct }) {
  const r = co.report
  const [펼침, set펼침] = useState(() => new Set())   // 처음엔 전부 접혀 있다
  const 토글 = (k) => set펼침((s) => {
    const n = new Set(s)
    if (n.has(k)) n.delete(k); else n.add(k)
    return n
  })
  const 자금 = 자금계좌(r.accounts)
  const 원화 = 자금.filter(원화인가)
  const 외화 = 자금.filter((a) => !원화인가(a))
  const 대체건수 = (r.transfers || []).length
  return (
    <div className="td-report">
      <div className="print-only td-print-co">{co.name}</div>
      <총보유 회사={co.name} 원화={원화} 외화={외화}
        대출={r.loanTotals?.['금일잔액'] || 0} 날짜={r.date} />
      <원화영역 rows={원화} 기간={기간} 회사={co.name}
        펼침={펼침} 토글={토글} onOpenAcct={onOpenAcct} />
      <외화영역 rows={외화} 기간={기간} 환전평균={r.환전평균}
        펼침={펼침} 토글={토글} onOpenAcct={onOpenAcct} />
      <차입금영역 loanTotals={r.loanTotals} accounts={r.accounts} onOpenAcct={onOpenAcct} />
      {!!대체건수 && (
        <섹션 제목="대체(이체) 내역" 단위="원"
          꼬리="우리 계좌끼리 오간 돈입니다 · 위 입금·출금에는 들어 있지 않습니다">
          <접는칸 제목={`${기간 ? '기간' : '당일'} 대체 ${대체건수}건`}>
            <table className="td-rep" style={{ maxWidth: 620 }}>
              <thead><tr>
                {기간 && <th className="c" style={{ width: 108 }}>일자</th>}
                <th className="l">계좌</th><th className="c">구분</th><th>금액</th>
              </tr></thead>
              <tbody>
                {(r.transfers || []).map((t, i) => (
                  <tr key={i}>
                    {기간 && <td className="c">{t['일자']}</td>}
                    <td className="l">{t['계좌']}</td>
                    <td className="c">{t['구분']}</td>
                    <td>{fmt(t['금액'])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </접는칸>
        </섹션>
      )}
    </div>
  )
}

// 회사별 한눈에 — 카드를 나란히. 총합은 만들지 않는다.
function 한눈에({ 회사들, 기간, onPick }) {
  return (
    <div className="td-cards">
      {회사들.map((co) => {
        const 자금 = 자금계좌(co.report.accounts)
        const s = 집계(자금)
        const f = 집계(자금.filter((a) => !원화인가(a))).기말
        const 대출 = co.report.loanTotals?.['금일잔액'] || 0
        const 증감 = s.기말 - s.기초
        return (
          <button type="button" className="td-card" key={co.code} onClick={() => onPick(co.code)}>
            <div className="td-card-name">{짧은회사(co.name)}</div>
            <div className="td-card-val">{fmt(s.기말)} <span className="td-kpi-unit">원</span></div>
            <div className={'td-card-delta' + (증감 < 0 ? ' neg' : 증감 > 0 ? ' pos' : '')}>
              {Math.abs(증감) < 0.5 ? '변동 없음'
                : `${증감 > 0 ? '▲' : '▼'} ${fmt(Math.abs(증감))}`}
            </div>
            <dl className="td-card-rows">
              <div><dt>기초잔액</dt><dd>{fmt(s.기초)}</dd></div>
              <div><dt>{기간 ? '총 입금' : '당일 입금'}</dt><dd>{fmt(s.입금)}</dd></div>
              <div><dt>{기간 ? '총 출금' : '당일 출금'}</dt><dd>{fmt(s.출금)}</dd></div>
              {!!f && <div><dt>그중 외화 환산</dt><dd>{fmt(f)}</dd></div>}
              {!!대출 && <div className="loan"><dt>차입금</dt><dd>{fmt(대출)}</dd></div>}
            </dl>
            <div className="td-card-go muted">자금일보 보기 →</div>
          </button>
        )
      })}
      <div className="muted td-note" style={{ gridColumn: '1 / -1' }}>
        법인이 다르면 남의 돈이라 합계를 내지 않습니다. 회사를 누르면 그 회사 자금일보로 갑니다.
      </div>
    </div>
  )
}

// ── 자금 대시보드 ────────────────────────────────────────────────────────────
//
// 자금일보는 하루를 세우는 화면이다 — 기초 + 입금 − 출금 = 기말이 맞는지 보는 자리라
// 표가 촘촘하고, 「요즘 어떻게 흘러왔나」와 「무엇으로 들고 있나」는 보이지 않는다.
// 그룹사 재무 TFT 미션1 에서 꿈비가 만든 자금 대시보드가 그 자리를 채운다 — 지표 줄,
// 일별 잔액 그래프, 유동성 구성, 전일 입출금, 차입 현황. 같은 구성으로 맞춘다.
// 회사끼리 견줄 때 화면이 다르면 같은 이름의 숫자가 다른 것을 뜻하게 된다.
//
// 숫자는 자금일보가 이미 만든 것을 그대로 쓴다. 여기서 다시 세면 두 화면의 「총 유동성」이
// 언젠가 갈리고, 그때 어느 쪽이 맞는지 아무도 모른다. 그래프만 따로 받는다(일별 추이).
//
// 단위는 **백만원**이다. 16억이 1,618,884,552 로 서면 다섯 칸을 눈으로 세야 한다.
// 다만 전일 입출금의 거래 한 줄은 원 그대로다 - 그 자리는 「얼마가 들어왔나」를
// 확인하는 자리라 끝자리까지 봐야 한다.

const 백만 = (v) => (Math.round((v || 0) / 1e5) / 10)
  .toLocaleString('ko-KR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

// MMT·신탁·예수금은 「당장 쓸 수 있는 통장 잔액」과 성격이 다르다. 증권 안에서 자리만
// 바꾸는 돈이라 입출금계좌와 섞으면 통장이 두둑해 보인다(CLAUDE.md 증권 흐름 참고).
const MMT유형 = new Set(['MMT·RP', 'MMT', '신탁', 'MMT·신탁', '증권 예수금', '예수금'])
const 합 = (rows, k = '금일잔액') => (rows || []).reduce((s, a) => s + (a[k] || 0), 0)

function 갈래(자금) {
  return {
    입출금: 자금.filter((a) => 원화인가(a) && !MMT유형.has(a['계좌유형'] || '')),
    MMT: 자금.filter((a) => 원화인가(a) && MMT유형.has(a['계좌유형'] || '')),
    외화: 자금.filter((a) => !원화인가(a)),
  }
}

// 사업부로 나눈 금액. 차례는 사람이 정한 차례를 따른다 - 금액 순으로 세우면 날마다
// 자리가 바뀌어 늘 보던 자리에서 못 찾는다.
function 사업부나눔(rows) {
  const m = new Map()
  for (const a of rows || []) {
    const u = a['사업부'] || '기타'
    m.set(u, (m.get(u) || 0) + (a['금일잔액'] || 0))
  }
  // 0 인 줄은 뺀다 - 「링크맘사업부 0.0」 같은 줄이 섞이면 읽을 것이 없는데도 눈이 센다
  return [...m.entries()]
    .filter(([, v]) => Math.abs(v) >= 0.5)
    .sort((a, b) => 사업부순(a[0]) - 사업부순(b[0]) || b[1] - a[1])
    .map(([u, v]) => [사업부이름(u), v])
}

const 색차례 = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6']

function KPI({ 이름, 값, 꼬리, 나눔, 강조, 빨강 }) {
  return (
    <div className={'tdb-kpi' + (강조 ? ' lead' : '')}>
      <div className="tdb-kpi-h">{이름}</div>
      <div className={'tdb-kpi-v' + (빨강 ? ' bad' : '')}>{값}</div>
      <div className="tdb-kpi-s muted">{꼬리}</div>
      {!!나눔?.length && (
        <dl className="tdb-kpi-parts">
          {나눔.map(([n, v], i) => (
            <div key={n}>
              <dt><i className={'tdb-dot ' + 색차례[i % 색차례.length]} />{n}</dt>
              <dd>{백만(v)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

// 일별 잔액 그래프. 바닥을 0 으로 잡는다 - 최저값을 바닥으로 두면 1% 움직인 날이
// 절벽처럼 보인다. 잔액 그래프는 0 에서 재야 크기가 눈에 들어온다.
function 추이그림({ points, 기준일 }) {
  if (!points || points.length < 2) return <div className="muted">아직 그릴 자료가 없습니다.</div>
  const W = 680
  const H = 190
  const vs = points.map((p) => p.잔액)
  const 위 = Math.max(...vs, 1) * 1.08
  const x = (i) => (i / (points.length - 1)) * W
  const y = (v) => H - (v / 위) * H
  const 선 = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.잔액).toFixed(1)}`).join(' ')
  const 면 = `${선} L${W.toFixed(1)},${H} L0,${H} Z`
  const 끝 = points[points.length - 1]
  const 앞 = points[points.length - 2]
  const 증감 = 끝.잔액 - 앞.잔액
  // 가로축 눈금은 달이 바뀌는 날만. 날짜를 다 찍으면 글자가 겹쳐 아무것도 안 읽힌다.
  const 달바뀜 = points.map((p, i) => [p, i]).filter(([p], i) => i > 0 && p.일자.slice(8) === '01')
  const 억 = (v) => `${Math.round(v / 1e8)}억`
  return (
    <>
      <div className="tdb-chart">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="tdb-svg">
          {[0.25, 0.5, 0.75].map((f) => (
            <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} className="tdb-grid" />
          ))}
          <path d={면} className="tdb-area" />
          <path d={선} className="tdb-line" />
          <circle cx={W} cy={y(끝.잔액)} r="3.5" className="tdb-dot-end" />
        </svg>
        <div className="tdb-yaxis">
          <span>{억(위)}</span><span>{억(위 / 2)}</span><span>0</span>
        </div>
      </div>
      <div className="tdb-xaxis">
        <span>{points[0].일자.slice(5).replace('-', '/')}</span>
        {달바뀜.slice(-3).map(([p, i]) => (
          <span key={p.일자} style={{ left: `${(i / (points.length - 1)) * 100}%` }}>
            {p.일자.slice(5).replace('-', '/')}
          </span>
        ))}
        <span className="r">{끝.일자.slice(5).replace('-', '/')}</span>
      </div>
      <div className="muted tdb-chart-foot">
        {기준일} 잔액 <b>{백만(끝.잔액)}</b>백만원 · 전일 대비{' '}
        <span className={증감 >= 0 ? 'ok' : 'warn'}>
          {증감 >= 0 ? '+' : '−'}{백만(Math.abs(증감))}
        </span>
        {' · 최저 '}{백만(Math.min(...vs))}{' / 최고 '}{백만(Math.max(...vs))}
      </div>
    </>
  )
}

// 비중 막대. 금액이 큰 것부터 - 구성은 차례가 뜻을 갖지 않아 큰 것이 위에 와야 읽힌다.
function 비중줄({ 제목, 줄, 합계, 오른쪽 }) {
  const 전체 = 합계 || 줄.reduce((s, [, v]) => s + v, 0) || 1
  return (
    <div className="tdb-mix">
      <div className="tdb-mix-h">{제목}{오른쪽 && <span className="muted">{오른쪽}</span>}</div>
      {줄.map(([n, v], i) => (
        <div className="tdb-mix-row" key={n}>
          <span className="n"><i className={'tdb-dot ' + 색차례[i % 색차례.length]} />{n}</span>
          <span className="bar"><i style={{ width: `${Math.max(0, (v / 전체) * 100)}%` }} /></span>
          <span className="p muted">{((v / 전체) * 100).toFixed(1)}%</span>
          <span className="v">{백만(v)}</span>
        </div>
      ))}
    </div>
  )
}


// 차입 현황 — 잔액(클로브, 매일)과 조건(엑셀)을 서버가 짝지어 준다.
// 금리를 모르는 차입을 0% 로 쳐서 평균에 넣으면 평균이 조용히 낮아진다. 그래서
// 가중평균 금리는 조건이 있는 줄만 세고, 모르는 것은 「조건 없는 n건」으로 따로 말한다.
const 퍼센트 = (v) => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(2)}%`)

function 디데이({ 날 }) {
  if (날 === null || 날 === undefined) return <span className="muted">—</span>
  const 급한가 = 날 <= 90
  return <span className={'tdb-dday' + (급한가 ? ' hot' : '')}>D{날 >= 0 ? '-' : '+'}{Math.abs(날)}</span>
}

function 차입현황({ 자료, 올리는중, onUpload }) {
  const 고르기 = React.useRef(null)
  const s = 자료?.요약 || {}
  const 줄 = 자료?.loans || []
  return (
    <section className="panel tdb-loanpanel">
      <div className="tdb-h">
        차입 현황
        <span className="muted">
          잔액은 클로브 일일 · 조건은 차입현황 엑셀
          {s.기준일 ? ` (${s.기준일} 기준)` : ''}
          <button type="button" className="tdb-up" disabled={올리는중}
            onClick={() => 고르기.current?.click()}>
            {올리는중 ? '읽는 중…' : '조건 엑셀 올리기'}
          </button>
          <input type="file" ref={고르기} accept=".xlsx,.xls" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(f) }} />
        </span>
      </div>
      <div className="tdb-loankpis">
        <div><dt>차입금 잔액</dt><dd>{백만(s.잔액)}</dd><small className="muted">{줄.length}건</small></div>
        <div><dt>가중평균 금리</dt><dd>{퍼센트(s.가중금리)}</dd>
          <small className="muted">조건 등록 {줄.length - (s.조건없음 || 0)}/{줄.length}건</small></div>
        <div><dt>월 이자비용 (추정)</dt><dd>{s.월이자 ? 백만(s.월이자) : '—'}</dd>
          <small className="muted">잔액 × 금리 ÷ 12</small></div>
        <div className={s.구십일내건수 ? 'hot' : undefined}>
          <dt>1년 내 만기</dt><dd>{백만(s.일년내만기)}</dd>
          <small className="muted">{s.일년내건수 || 0}건 · 90일 내 {s.구십일내건수 || 0}건</small></div>
      </div>
      {줄.length === 0 ? <div className="muted">차입이 없습니다.</div> : (
        <table className="td-rep tdb-loantable">
          <thead><tr>
            <th className="l">은행 / 대출과목</th>
            <th style={{ width: 110 }}>잔액</th>
            <th style={{ width: 76 }}>금리</th>
            <th className="c" style={{ width: 160 }}>만기</th>
            <th className="c" style={{ width: 78 }}>상환</th>
            <th className="l" style={{ width: 150 }}>차입기간</th>
            <th className="l" style={{ width: 120 }}>담보 · 보증</th>
          </tr></thead>
          <tbody>
            {줄.map((r, i) => (
              <tr key={i}>
                <td className="l">
                  <b>{r.은행} · {r.이름}</b>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {r.종별 || (r.조건있음 ? '' : '조건이 등록되지 않았습니다')}
                    {r.계정과목 ? ` · ${r.계정과목}` : ''}
                  </div>
                </td>
                <td className="lead">{백만(r.잔액)}</td>
                <td>{퍼센트(r.이자율)}</td>
                <td className="c">
                  {r.만기일 || <span className="muted">—</span>}{' '}
                  <디데이 날={r.남은날} />
                </td>
                <td className="c">{r.상환방식 || <span className="muted">—</span>}</td>
                <td className="l muted" style={{ fontSize: 11.5 }}>
                  {r.시작일 ? `${r.시작일} ~ ${r.만기일}` : '—'}
                </td>
                <td className="l muted" style={{ fontSize: 11.5 }}>
                  {[r.담보, r.보증].filter(Boolean).join(' · ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="muted tdb-foot">
        단위 백만원. {s.조건없음
          ? <b>조건이 없는 차입이 {s.조건없음}건 있습니다 — 엑셀을 올리면 채워집니다.</b>
          : '담보·보증인 칸은 차입현황 엑셀에 없어 비어 있습니다.'}
        {s.올린때 ? ` · 조건 올린 때 ${String(s.올린때).slice(0, 16).replace('T', ' ')}` : ''}
      </div>
    </section>
  )
}

// 계좌별 잔액 — 꿈비 ④번 화면과 같은 구성(은행별 접기·펼치기 / 유형·사업부 거르개).
//
// **접어 둔 채로 연다.** 자금일보 대시보드를 그대로 찍어 보고서로 올리는데, 계좌 42개
// 잔액까지 그림에 들어가면 임원 보고가 계좌 단위 장부가 된다. 노션에 올리던 때도 같은
// 이유로 접은 채 찍었다 - 임원이 보는 것은 그 윗단이다. 찍는 쪽(screenshot.py)이
// 접는칸을 펴는 것은 자금일보 표를 찍을 때뿐이다.
//
// 자금일보 표에도 계좌는 있다(사업부·통화 줄을 누르면 펴진다). 다만 거기서는 보고 양식의
// 차례대로 서 있어서 «국민은행에 얼마 있나»를 세려면 줄을 눈으로 모아야 한다. 여기서는
// 은행으로 묶고 유형·사업부로 거른다 - 같은 숫자를 다른 눈으로 보는 자리다.
function 계좌별잔액({ 자금, onOpenAcct }) {
  const [유형, set유형] = useState('')
  const [부서, set부서] = useState('')
  const [편은행, set편은행] = useState(() => new Set())
  const 유형들 = [...new Set(자금.map((a) => a['계좌유형'] || '기타'))]
  const 부서들 = [...new Set(자금.map((a) => a['사업부'] || '기타'))]
  const 보임 = 자금.filter((a) => (!유형 || (a['계좌유형'] || '기타') === 유형)
    && (!부서 || (a['사업부'] || '기타') === 부서))

  const 묶음 = []
  for (const a of [...보임].sort(계좌순)) {
    const 은 = String(a['계좌'] || '').split(' / ')[0].trim() || '기타'
    let g = 묶음.find((x) => x.은행 === 은)
    if (!g) { g = { 은행: 은, 계좌: [], 합: 0 }; 묶음.push(g) }
    g.계좌.push(a)
    g.합 += a['금일잔액'] || 0
  }
  묶음.sort((x, y) => y.합 - x.합)
  const 전체합 = 묶음.reduce((s, g) => s + g.합, 0)
  const 토글 = (은) => set편은행((s) => {
    const n = new Set(s)
    if (n.has(은)) n.delete(은); else n.add(은)
    return n
  })

  return (
    <section className="panel tdb-accts">
      <접는칸 제목={`계좌별 잔액 ${보임.length}개`}
        꼬리={`${백만(전체합)} 백만원 · 은행을 누르면 계좌가 펼쳐집니다`}>
        <div className="tdb-filters">
          <span className="muted">유형</span>
          <button type="button" className={'nv-chip' + (유형 ? '' : ' on')}
            onClick={() => set유형('')}>전체</button>
          {유형들.map((t) => (
            <button key={t} type="button" className={'nv-chip' + (유형 === t ? ' on' : '')}
              onClick={() => set유형(유형 === t ? '' : t)}>{t}</button>
          ))}
          <span className="tdb-filter-sep" />
          <span className="muted">사업부</span>
          <button type="button" className={'nv-chip' + (부서 ? '' : ' on')}
            onClick={() => set부서('')}>전체</button>
          {부서들.map((u) => (
            <button key={u} type="button" className={'nv-chip' + (부서 === u ? ' on' : '')}
              onClick={() => set부서(부서 === u ? '' : u)}>{사업부이름(u)}</button>
          ))}
        </div>
        <table className="td-rep tdb-accttable">
          <thead><tr>
            <th className="l">은행 / 계좌</th>
            <th className="l" style={{ width: 110 }}>유형</th>
            <th className="l" style={{ width: 110 }}>사업부</th>
            <th style={{ width: 130 }}>외화 잔액</th>
            <th style={{ width: 120 }}>잔액</th>
          </tr></thead>
          <tbody>
            {묶음.map((g) => (
              <React.Fragment key={g.은행}>
                <tr className="tdb-bank" onClick={() => 토글(g.은행)}>
                  <td className="l">
                    <span className="tdb-arrow">{편은행.has(g.은행) ? '▾' : '▸'}</span>
                    <b>{g.은행}</b>
                    <span className="muted"> · 계좌 {g.계좌.length}개</span>
                  </td>
                  <td colSpan={3} />
                  <td className="lead">{백만(g.합)}</td>
                </tr>
                {편은행.has(g.은행) && g.계좌.map((a) => (
                  <tr key={a['계좌']} className={a['거래']?.length ? 'acct-open' : undefined}
                    onClick={() => a['거래']?.length && onOpenAcct(a)}>
                    <td className="l tdb-acctname">
                      {a['계좌']}
                      {a['별칭'] && <span className="muted"> · {a['별칭']}</span>}
                    </td>
                    <td className="l muted">{a['계좌유형'] || '기타'}</td>
                    <td className="l muted">{사업부이름(a['사업부'])}</td>
                    <td className="muted">
                      {원화인가(a) ? '—'
                        : `${(a['외화잔액'] || 0).toLocaleString('ko-KR', { maximumFractionDigits: 2 })} ${a['통화']}`}
                    </td>
                    <td>{백만(a['금일잔액'])}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {!묶음.length && <tr><td colSpan={5} className="c muted">거르개에 걸리는 계좌가 없습니다</td></tr>}
          </tbody>
        </table>
        <div className="muted tdb-foot">
          단위 백만원 · 외화 잔액만 통화 단위입니다. 거래가 있는 계좌는 눌러서 그날 내역을 봅니다.
        </div>
      </접는칸>
    </section>
  )
}

function 대시보드({ co, 기간, 추이, 차입자료, 올리는중, onUpload, onOpenAcct, goto }) {
  const r = co.report
  const 자금 = 자금계좌(r.accounts)
  const g = 갈래(자금)
  const 유동성 = 합(자금)
  const 전일 = 합(자금, '전일잔액')
  const 대출들 = (r.accounts || []).filter(대출인가)
    .filter((a) => (a['금일잔액'] || 0) || (a['전일잔액'] || 0)).sort(계좌순)
  const 차입 = r.loanTotals?.['금일잔액'] || 0
  const 순차입 = 차입 - 유동성
  const 증감 = 유동성 - 전일

  // 전일 입출금 — 계좌끼리 오간 돈(대체)은 뺀다. 들어온 적 없는 돈이 입금으로 서면
  // 그 하루가 통째로 부풀어 보인다(10/06 에 3.5억 중 1.21억이 그랬다).
  const 거래들 = []
  for (const a of 자금) {
    for (const t of a['거래'] || []) {
      if ((t['구분'] || '').includes('대체')) continue
      거래들.push({ ...t, 계좌: a['별칭'] || a['계좌'] })
    }
  }
  거래들.sort((x, y) => Math.abs(y['금액']) - Math.abs(x['금액']))
  const 입금 = r.totals?.['금일입금현금'] || 0
  const 출금 = r.totals?.['금일출금현금'] || 0

  const 은행별 = (() => {
    const m = new Map()
    for (const a of 자금) {
      const b = String(a['계좌'] || '').split(' / ')[0].trim() || '기타'
      m.set(b, (m.get(b) || 0) + (a['금일잔액'] || 0))
    }
    return [...m.entries()].filter(([, v]) => Math.abs(v) >= 0.5).sort((x, y) => y[1] - x[1])
  })()
  const 유형별 = [
    ['입출금계좌', 합(g.입출금)], ['MMT·신탁', 합(g.MMT)], ['외화예금', 합(g.외화)],
  ].filter(([, v]) => Math.abs(v) >= 0.5)

  return (
    <div className="tdb">
      <div className="tdb-kpis">
        <KPI 강조 이름="총 유동성 (예금+증권)" 값={백만(유동성)}
          꼬리={Math.abs(증감) < 0.5 ? (기간 ? '기간 변동 없음' : '전일 비교 없음')
            : `${기간 ? '기간' : '전일'} 대비 ${증감 > 0 ? '+' : '−'}${백만(Math.abs(증감))}`}
          나눔={사업부나눔(자금)} />
        <KPI 이름="입출금계좌" 값={백만(합(g.입출금))}
          꼬리={`원화 보통·자유예금 ${g.입출금.length}개`} 나눔={사업부나눔(g.입출금)} />
        <KPI 이름="MMT·신탁 / 외화" 값={백만(합(g.MMT) + 합(g.외화))}
          꼬리={`MMT·신탁 ${백만(합(g.MMT))} · 외화 ${백만(합(g.외화))}`}
          나눔={사업부나눔([...g.MMT, ...g.외화])} />
        <KPI 이름="차입금" 값={백만(차입)} 꼬리={`${대출들.length}건 · 갚아야 할 돈`}
          나눔={사업부나눔(대출들)} />
        <KPI 이름="순차입금" 값={백만(Math.abs(순차입))} 빨강={순차입 > 0}
          꼬리={순차입 > 0 ? '차입금 − 총 유동성' : '유동성이 차입금보다 많습니다'} 나눔={[]} />
      </div>

      <div className="tdb-row">
        <section className="panel tdb-main">
          <div className="tdb-h">
            원화 입출금 일별 잔액
            <span className="muted">
              외화·대출은 뺐습니다 — 수집에 환율이 1.0 으로 와서 원화로 더할 수 없습니다
            </span>
          </div>
          {추이 === null ? <div className="muted">그래프를 그리는 중…</div>
            : <추이그림 points={추이} 기준일={r.date} />}
        </section>

        <section className="panel tdb-side">
          <비중줄 제목="유동성 구성" 줄={유형별} 합계={유동성} 오른쪽={`${백만(유동성)} 백만원`} />
          <비중줄 제목="사업부별" 줄={사업부나눔(자금)} 합계={유동성} />
          <비중줄 제목="은행별 예금" 줄={은행별} 합계={유동성} />
        </section>
      </div>

      <div className="tdb-row">
        <section className="panel tdb-main">
          <div className="tdb-h">
            {기간 ? '기간 입출금' : '전일 입출금'}
            <span className="muted">{r.date} · 계좌끼리 오간 대체는 뺐습니다</span>
          </div>
          <div className="tdb-io">
            <div className="in"><dt>입금</dt><dd>{백만(입금)}</dd></div>
            <div className="out"><dt>출금</dt><dd>{백만(출금)}</dd></div>
            <div className="net"><dt>순유입</dt>
              <dd>{입금 - 출금 >= 0 ? '+' : '−'}{백만(Math.abs(입금 - 출금))}</dd></div>
          </div>
          {거래들.length === 0 ? <div className="muted">그날 움직인 돈이 없습니다.</div> : (
            <table className="td-rep tdb-tx">
              <thead><tr>
                <th className="c" style={{ width: 54 }}>시각</th>
                <th className="l">계좌</th><th className="l">적요</th>
                <th style={{ width: 130 }}>금액(원)</th>
              </tr></thead>
              <tbody>
                {거래들.slice(0, 8).map((t, i) => (
                  <tr key={i}>
                    <td className="c muted">{t['시각']}</td>
                    <td className="l">{t['계좌']}</td>
                    <td className="l">{t['상대']}</td>
                    <td className={t['금액'] >= 0 ? 'ok' : undefined}>
                      {t['금액'] >= 0 ? '+' : '−'}{fmt(Math.abs(t['금액']))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {거래들.length > 8 && (
            <div className="muted tdb-foot">금액 상위 8건입니다 · 모두 {거래들.length}건</div>
          )}
        </section>

        <section className="panel tdb-side">
          <div className="tdb-h">자금 수지<span className="muted">{r.date}</span></div>
          <비중줄 제목="당일 움직임" 줄={[['입금', 입금], ['출금', 출금]]}
            합계={Math.max(입금, 출금) || 1} />
          <div className="tdb-netbox">
            <dt>순유입</dt>
            <dd className={입금 - 출금 >= 0 ? 'ok' : 'bad'}>
              {입금 - 출금 >= 0 ? '+' : '−'}{백만(Math.abs(입금 - 출금))}
            </dd>
          </div>
          <button type="button" className="tdb-go" onClick={() => goto && goto('fund-plan')}>
            월 자금계획 보기 →
          </button>
          <button type="button" className="tdb-go" onClick={() => goto && goto('treasury-fx')}>
            외화예금 보기 →
          </button>
        </section>
      </div>

      <계좌별잔액 자금={자금} onOpenAcct={onOpenAcct} />

      <차입현황 자료={차입자료} 올리는중={올리는중} onUpload={onUpload} />

      <div className="muted tdb-note">
        단위는 백만원입니다(거래 한 줄만 원). 숫자는 자금일보와 같은 자리에서 나옵니다 —
        여기서 다시 세지 않습니다. <b>금리·만기·담보·보증</b>과 <b>월 자금계획 대비 실적</b>은
        아직 받아 둔 자료가 없어 비어 있습니다.
      </div>
    </div>
  )
}

export default function TreasuryDaily({ currentUserId, autoOpenNoteId, onAutoOpened, goto }) {
  const 처음 = 주소조건()
  const [date, setDate] = useState(() => 처음?.date || (처음?.range ? 처음.range[1] : yesterday()))
  // 하루가 기본이다. 기간은 월 마감처럼 한 달을 통째로 볼 때만 쓰므로 눌러서 켠다.
  const [range, setRange] = useState(() => 처음?.range || null)   // null 이면 하루
  // 회사는 한 번에 다 받아 두고 탭은 화면 안에서만 바꾼다. 주소를 바꾸면(회사 전환기)
  // 페이지가 다시 떠서 보던 날짜를 잃는다 - 자금일보는 날짜를 짚어 가며 보는 화면이다.
  const [회사코드, set회사코드] = useState(() => currentCompany())
  // 보기는 셋이다 - 회사 탭이 «어느 회사», 이 단추가 «무엇을» 이다.
  // 대시보드가 처음 뜬다. 자금일보 표를 그대로 찍어 보고서로 올리므로 찍는 쪽
  // (fa/screenshot.py)이 「자금일보」 단추를 먼저 누르고 .td-report 를 기다린다.
  const [보기, set보기] = useState('dash')      // dash · report · all
  const 한눈에보기 = 보기 === 'all'
  const 대시보드보기 = 보기 === 'dash'
  const [추이, set추이] = useState(null)
  const [차입자료, set차입자료] = useState(null)
  const [차입올림, set차입올림] = useState(null)   // {올리는중} | {건수} | {error}
  const [coErr, setCoErr] = useState([])     // 못 만든 회사
  const 기간 = !!range
  const 시작일 = range ? range[0] : date
  const 끝일 = range ? range[1] : date
  // 지금 보고 있는 기간이 어느 단추인지. 달력으로 직접 집으면 아무것도 안 켜진다 -
  // 그게 맞다. 「월」이 켜져 있는데 기간이 월초~월말이 아니면 거짓말이 된다.
  const 기간범위 = (() => {
    if (!기간) return 'day'
    for (const [k] of PERIODS) {
      if (k === 'day') continue
      const [a2, b2] = 기간잡기(k, 끝일)
      if (a2 === 시작일 && b2 === 끝일) return k
    }
    return null
  })()
  const [회사들, set회사들] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  // 눌러서 연 계좌. 소계만 보고 "이 8천만 원이 뭐지" 하고 다른 화면으로 건너가지 않게,
  // 팝업으로 그날 거래를 보여 준다 - 표를 밀어내지 않아 보던 자리를 잃지 않는다.
  const [openAcct, setOpenAcct] = useState(null)
  const [err, setErr] = useState('')
  const [노션, set노션] = useState(null)   // {보내는중} | {제목, url} | {error}
  const [최신화막힘, set최신화막힘] = useState(null)
  // 아침 수집에서 못 받은 것. 조용히 비어 있으면 아무도 모른다 - 이틀 연속 링크맘이
  // 빠졌는데 화면은 평소와 똑같아 보였다(2026-10-19·20).
  const [수집빠짐, set수집빠짐] = useState([])
  const [보고서, set보고서] = useState(null)   // {만드는중} | {id, 제목} | {error}

  // 화면을 띄운 뒤에 주소를 바꿔도 따라간다 - 찍는 쪽은 메뉴를 먼저 누르고
  // 그 다음에 날짜를 건네므로, 처음 한 번만 읽으면 놓친다.
  useEffect(() => {
    const 바뀜 = () => {
      const c = 주소조건()
      if (!c) return
      if (c.range) { setRange(c.range); setDate(c.range[1]) } else { setRange(null); setDate(c.date) }
    }
    window.addEventListener('hashchange', 바뀜)
    return () => window.removeEventListener('hashchange', 바뀜)
  }, [])

  const 고른회사 = (회사들 || []).find((c) => c.code === 회사코드) || (회사들 || [])[0] || null
  const data = 고른회사?.report || null
  const lastSynced = 고른회사?.lastSynced || null
  // 동기화는 주소가 들고 있는 회사에만 보낼 수 있다(X-Company). 남의 회사 탭에서 누르면
  // 엉뚱한 DB 를 긁으므로 잠근다.
  const 여기회사 = currentCompany()

  const 받기 = (조용히) => {
    if (!조용히) { setErr(''); set회사들(null); setCoErr([]) }
    return portal.get(`/api/treasury/daily-report/all?date=${끝일}` + (기간 ? `&from=${시작일}` : ''))
      .then((r) => {
        const 된곳 = (r.companies || []).filter((c) => c.report)
        // 계좌가 한 줄도 없는 회사도 알려야 한다. 오류가 아니라 그냥 비어 있는 것이라
        // 아무 말 없이 빠지면 "그 회사 돈이 0원"으로 읽힌다 - 수집 전일 뿐이다.
        setCoErr([
          ...(r.companies || []).filter((c) => !c.report)
            .map((c) => ({ name: c.name, 왜: '만들지 못했습니다' })),
          ...된곳.filter((c) => !c.report.accounts.length)
            .map((c) => ({ name: c.name, 왜: '계좌 자료가 없습니다 (수집 전)' })),
        ])
        set회사들(된곳)
        return 된곳
      })
      .catch((e) => setErr(e.status === 403
        ? '자금일보 화면 권한이 없습니다. 관리자에게 권한을 요청하세요.' : e.message))
  }

  // 캐시 DB에서 즉시 표시, 오래됐으면 자동 동기화 (클로브 → 우리 DB → 화면)
  const doSync = async () => {
    setSyncing(true)
    try {
      const r = await portal.post('/api/treasury/daily-report/sync',
        기간 ? { date: 끝일, from: 시작일 } : { date: 끝일 })
      // 최신화가 막혀도 숫자는 받아 둔 것으로 선다. 빨간 줄 대신 한 줄로만 알린다 -
      // 못 받은 것과 못 보는 것은 다른 일이다.
      set최신화막힘(r?.syncError || null)
      await 받기(true)
    } catch (e) { setErr(e.message) } finally { setSyncing(false) }
  }

  useEffect(() => {
    if (!대시보드보기 || !고른회사) return
    set추이(null)
    portal.get(`/api/treasury/balance-trend?to=${끝일}&days=70&company=${고른회사.code}`)
      .then((r) => set추이(r.points || [])).catch(() => set추이([]))
  }, [대시보드보기, 고른회사?.code, 끝일])

  // 차입은 날짜를 타지 않는다(잔액은 클로브가 준 오늘 값, 조건은 엑셀) - 회사만 본다
  const 차입받기 = (code) => portal.get(`/api/treasury/loans?company=${code}`)
    .then(set차입자료).catch(() => set차입자료({ loans: [], 요약: {} }))
  useEffect(() => {
    if (!대시보드보기 || !고른회사) return
    set차입자료(null)
    차입받기(고른회사.code)
  }, [대시보드보기, 고른회사?.code])

  const 차입조건올리기 = async (file) => {
    set차입올림({ 올리는중: true })
    try {
      const r = await portal.upload('/api/treasury/loans/terms', {}, [file])
      set차입올림(r)
      await 차입받기(고른회사.code)
    } catch (e) { set차입올림({ error: e.message }) }
  }

  useEffect(() => {
    portal.get('/api/treasury/sync-status')
      .then((r) => set수집빠짐(r.빠짐 || [])).catch(() => {})
  }, [])

  useEffect(() => {
    받기().then((된곳) => {
      if (!된곳) return
      // 오늘·어제만 장중에 거래가 더 생길 수 있어, 캐시가 오래됐으면 한 번 받아온다.
      // 지난 날짜는 캐시에 다 있으므로 날짜를 넘길 때마다 기다리게 하지 않는다.
      const 나 = 된곳.find((c) => c.code === 여기회사)
      const 최근 = (Date.now() - new Date(`${끝일}T23:59:59`).getTime()) < 2 * 86400 * 1000
      const stale = !나?.lastSynced || Date.now() - new Date(나.lastSynced).getTime() > 10 * 60 * 1000
      if (나 && 최근 && stale) doSync()
    })
  }, [시작일, 끝일, 기간])

  const 검증 = data?.transferCheck

  return (
    <>
      <h2 className="no-print td-title">자금일보</h2>
      {/* 인쇄 표지 — 화면 제목과 조작 단추는 인쇄에서 빠진다 */}
      <div className="print-only print-head">
        <div className="t">자금일보{한눈에보기 ? '' : ` · ${짧은회사(고른회사?.name)}`}</div>
        <div className="s">
          {기간 ? `기간 ${시작일} ~ ${끝일}` : `기준일 ${끝일}`}
          {검증 && ` · 대체 검증 ${검증.balanced ? '일치' : '불일치'}`}
          {lastSynced && ` · 계좌 데이터 ${String(lastSynced).slice(0, 16).replace('T', ' ')} 기준`}
        </div>
        <div className="line" />
      </div>

      <div className="td-bar no-print">
        {/* 법인 단위로 먼저 고른다. 전사 합산은 만들지 않는다 - 대표 지표가 될 수 없다. */}
        <div className="td-cos">
          {(회사들 || []).map((c) => (
            <button key={c.code} type="button"
              className={!한눈에보기 && c.code === 고른회사?.code ? 'on' : ''}
              onClick={() => set회사코드(c.code)}>
              {짧은회사(c.name)}
            </button>
          ))}
          {!!회사들?.length && (
            <>
              <span className="td-cos-sep" />
              <button type="button" className={'wide ' + (대시보드보기 ? 'on' : '')}
                onClick={() => set보기('dash')}>대시보드</button>
              <button type="button" className={'wide ' + (보기 === 'report' ? 'on' : '')}
                onClick={() => set보기('report')}>자금일보</button>
              <button type="button" className={'wide ' + (한눈에보기 ? 'on' : '')}
                onClick={() => set보기('all')}>회사별 한눈에</button>
            </>
          )}
        </div>
        <div className="td-when">
          <div className="seg-btns">
            {PERIODS.map(([k, label]) => (
              <button key={k} type="button" className={기간범위 === k ? 'on' : ''}
                onClick={() => {
                  if (k === 'day') { setDate(끝일); setRange(null); return }
                  setRange(기간잡기(k, 끝일))
                }}>{label}</button>
            ))}
          </div>
          {기간
            ? <DateRange start={시작일} end={끝일} onChange={(s, e) => setRange([s, e])} />
            : <DatePick value={date} onChange={setDate} />}
        </div>
        <div className="td-acts">
          <button type="button" className="btn secondary" disabled={!회사들}
            title="브라우저 인쇄 창에서 대상을 'PDF로 저장'으로 고르세요"
            onClick={() => window.print()}>PDF</button>
          {/* 아침 09:40 에 타이머가 알아서 올리지만, 그때 한투 엑셀이 아직 안 올라와
              있으면 증권이 빠진 숫자가 임원 보고로 나간다. 한투를 올린 뒤 스스로 다시
              보낼 길이 있어야 한다 - 같은 제목이면 갈아 끼우므로 두 번 올라가지 않는다. */}
          {/* 노션을 더 쓰지 않기로 해서 보고서는 우리 쪽에 쌓는다. 만들기만 여기서 하고
              경영자 페이지로 넘기는 일은 「보고서」 메뉴에서 한다 - 이 화면은 숫자를 보는
              자리이지 결재를 돌리는 자리가 아니다.
              노션에 올리던 그 모양 그대로, 이 화면을 찍어 담는다. 그래서 1분쯤 걸린다 -
              단추 글자에 그렇게 적어 둔다(기다리다 두 번 누르는 일이 있다). */}
          <button type="button" className="btn"
            disabled={!회사들 || 보고서?.만드는중}
            title={`${기간 ? `${시작일} ~ ${끝일}` : 끝일} 자금일보를 보고서로 만듭니다 — 대시보드·표·회사별을 찍어 담으므로 1분쯤 걸립니다`}
            onClick={async () => {
              set보고서({ 만드는중: true })
              try {
                const r = await portal.post('/api/reports/build',
                  기간 ? { kind: 'treasury', date: 끝일, from: 시작일 }
                       : { kind: 'treasury', date: 끝일 })
                set보고서(r)
              } catch (e) { set보고서({ error: e.message }) }
            }}>
            {보고서?.만드는중 ? '화면을 찍는 중… (1분쯤)' : '보고서 만들기'}
          </button>
          <button type="button" className="btn secondary"
            disabled={!회사들 || 노션?.보내는중}
            title={`노션 「에르모어 자금 DB」에 ${기간 ? `${시작일} ~ ${끝일}` : 끝일} 보고를 올립니다`}
            onClick={async () => {
              const 범위 = 기간 ? `${시작일} ~ ${끝일}` : 끝일
              if (!window.confirm(`노션에 ${범위} 자금일보를 올립니다.
`
                + '이미 같은 날짜 보고가 있으면 그 글을 새 숫자로 갈아 끼웁니다.')) return
              set노션({ 보내는중: true })
              try {
                const r = await portal.post('/api/treasury/notion-report',
                  { from: 시작일, to: 끝일 })
                set노션(r)
              } catch (e) { set노션({ error: e.message }) }
            }}>
            {노션?.보내는중 ? '노션에 올리는 중…' : '노션 보고'}
          </button>
          {/* 서랍도 같은 줄에. 제목 옆에 따로 떠 있으면 오른쪽 위가 두 겹으로 갈린다 */}
          <NoteDrawer target="treasury-daily" screenKey="treasury_daily_report"
            context={{ date }} contextLabel={date}
            onNavigate={(ctx) => ctx?.date && setDate(ctx.date)}
            autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
            currentUserId={currentUserId} />
          {/* 관리 기능은 보조 메뉴로 내린다. 보고 화면 맨 위에 설정 단추가 늘어서 있으면
              무엇이 숫자이고 무엇이 조작인지 눈이 먼저 가려내야 한다. */}
          <details className="td-more">
            <summary className="btn secondary">⋯ 관리</summary>
            <div className="td-more-menu">
              <button type="button" onClick={() => doSync()}
                disabled={syncing || 고른회사?.code !== 여기회사}
                title={고른회사 && 고른회사.code !== 여기회사
                  ? '동기화는 그 회사 주소에서만 됩니다' : ''}>
                {syncing ? '동기화 중…' : '지금 동기화'}
              </button>
              <button type="button"
                onClick={() => (goto ? goto('d-vendors', 'accounts') : setSettingsOpen(true))}>
                계좌 설정
              </button>
            </div>
          </details>
        </div>
      </div>

      <div className="td-meta no-print muted">
        <span>{기간 ? `${시작일} ~ ${끝일}` : 끝일} 기준</span>
        <span>·</span>
        <span>{syncing ? '최신 데이터를 받는 중'
          : lastSynced ? `데이터 ${String(lastSynced).slice(5, 16).replace('T', ' ')}` : '데이터 시각 없음'}</span>
        {!한눈에보기 && 검증 && (
          <>
            <span>·</span>
            <span className={검증.balanced ? 'ok' : (검증['외화시차'] ? 'muted' : 'warn')}
              title="계좌끼리 오간 돈은 나간 쪽과 들어온 쪽 합계가 같아야 합니다. 외화가 끼면 보내는 쪽 환전율과 받는 쪽 환산율이 달라 원화로는 조금 어긋납니다.">
              대체 검증 {검증.balanced ? '✓ 일치'
                : 검증['외화시차'] ? `외화 이체 진행중 ${fmt(검증['차이'])}`
                  : `⚠ 차액 ${fmt(검증['차이'])}`}
            </span>
          </>
        )}
      </div>

      {err && <div className="error-box">{err}</div>}
      {보고서 && !보고서.만드는중 && (
        <div className={'no-print td-notion ' + (보고서.error ? 'bad' : 'ok')}>
          {보고서.error
            ? `보고서를 만들지 못했습니다 — ${보고서.error}`
            : <>보고서 <b>{보고서.제목}</b> 를 {보고서.갈아끼움 ? '갈아 끼웠습니다' : '만들었습니다'}
                {보고서.그림 ? ` · 화면 ${보고서.그림}장을 담았습니다` : ' · 화면은 못 찍어 표만 담았습니다'}.
                「보고서」 메뉴에서 속성을 채운 뒤 넘기세요.</>}
        </div>
      )}
      {수집빠짐.length > 0 && (
        <div className="error-box no-print" style={{ marginBottom: 10 }}>
          <b>아침 수집에서 못 받은 것이 있습니다</b> —{' '}
          {[...new Set(수집빠짐.map((x) => x.회사이름))].join(' · ')}
          <span className="muted">
            {' '}({수집빠짐.map((x) => `${x.회사이름} ${x.종류}`).join(', ')})
          </span>
          <div className="muted" style={{ fontSize: 12 }}>
            그 회사 숫자는 마지막으로 받은 때까지만 맞습니다. 「⋯ 관리」의 동기화를 다시
            눌러 보시고, 그래도 안 되면 알려 주세요.
          </div>
        </div>
      )}
      {최신화막힘 && (
        <div className="hint no-print" style={{ marginBottom: 10 }}>
          <b>최신화를 못 했습니다</b> — 받아 둔 자료로 보여 줍니다(위 「데이터」 시각 기준).
          <span className="muted"> {최신화막힘}</span>
        </div>
      )}
      {노션 && !노션.보내는중 && (
        <div className={'no-print td-notion ' + (노션.error ? 'bad' : 'ok')}>
          {노션.error
            ? `노션에 올리지 못했습니다 — ${노션.error}`
            : <>노션에 <b>{노션.제목}</b> {노션.한것}.{' '}
                <a href={노션.url} target="_blank" rel="noreferrer">노션에서 보기</a></>}
          <button type="button" className="td-notion-x" onClick={() => set노션(null)}
            aria-label="닫기">×</button>
        </div>
      )}
      {coErr.length > 0 && (
        <div className="warn no-print" style={{ marginBottom: 10, fontSize: 13 }}>
          {coErr.map((c) => `${c.name}: ${c.왜}`).join(' · ')}
        </div>
      )}
      {!회사들 && !err && <div className="muted">불러오는 중…</div>}

      {회사들 && 대시보드보기 && 고른회사 && (
        <>
          {차입올림 && !차입올림.올리는중 && (
            <div className={'no-print td-notion ' + (차입올림.error ? 'bad' : 'ok')}>
              {차입올림.error
                ? `차입 조건을 올리지 못했습니다 — ${차입올림.error}`
                : `차입 조건 ${차입올림.건수}건을 올렸습니다 · ${차입올림.기준일} 기준`}
              <button type="button" className="td-notion-x" onClick={() => set차입올림(null)}
                aria-label="닫기">×</button>
            </div>
          )}
          <대시보드 key={고른회사.code} co={고른회사} 기간={기간} 추이={추이}
            차입자료={차입자료} 올리는중={!!차입올림?.올리는중} onUpload={차입조건올리기}
            onOpenAcct={setOpenAcct} goto={goto} />
        </>
      )}
      {회사들 && 한눈에보기 && (
        <>
          <한눈에 회사들={회사들} 기간={기간}
            onPick={(code) => { set보기('report'); set회사코드(code) }} />
          {/* 인쇄는 회사마다 쪽을 나눠 한 벌씩 낸다 - 카드만 찍힌 PDF 는 보고서가 아니다 */}
          <div className="print-only">
            {회사들.map((co) => (
              <div className="td-page" key={co.code}>
                <회사보고 co={co} 기간={기간} onOpenAcct={setOpenAcct} />
              </div>
            ))}
          </div>
        </>
      )}
      {회사들 && !한눈에보기 && !대시보드보기 && 고른회사 && (
        <회사보고 key={고른회사.code} co={고른회사} 기간={기간} onOpenAcct={setOpenAcct} />
      )}

      {settingsOpen && (
        <AccountSettingsModal onClose={() => setSettingsOpen(false)}
          onSaved={() => doSync()} />
      )}
      {openAcct && <AcctTxModal acct={openAcct} date={끝일} onClose={() => setOpenAcct(null)} />}
    </>
  )
}
